import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Box } from '@mui/material'
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Typography
} from '@carto/meridian-ds/components'
import { ApiError } from 'features/common'
import { useDeleteMultipleConnectionCredentialsMutation } from 'features/connections/hooks/connectionsHooks'
import { Connection, CredentialsMutationResponse } from 'features/connections/types'

type DeleteCredentialsDialogProps = {
  connections: Connection[]
  open: boolean
  onClose: (response?: string) => void
}

function DeleteCredentialsDialog({ connections, open, onClose }: DeleteCredentialsDialogProps) {
  const { mutateAsync: deleteMultipleCredentials, isLoading } = useDeleteMultipleConnectionCredentialsMutation()
  const [errors, setErrors] = useState<{ connectionId: string; error?: ApiError }[]>()

  const handleClickDelete = () => {
    const credentialsPayload: { connectionId: string }[] = connections.map((connection) => ({
      connectionId: connection.id
    }))
    deleteMultipleCredentials(credentialsPayload).then((data) => {
      const errors: CredentialsMutationResponse[] = data.filter((item: CredentialsMutationResponse) => item.error)
      if (errors.length === 0) {
        onClose('ok')
      } else {
        setErrors(errors)
      }
    })
  }

  return (
    <Dialog
      open={!!open}
      height="auto"
      onClose={() => onClose()}
      aria-labelledby="credentials-delete-form-dialog-title"
    >
      <DialogHeader
        id="credentials-delete-form-dialog-title"
        onClose={() => onClose()}
        title={<FormattedMessage id="connections.credentialsDialog.delete.title" />}
      />
      <DialogContent>
        <Typography variant="body2" color="textSecondary">
          {connections.length === 1 ? (
            <FormattedMessage
              id="connections.credentialsDialog.delete.question"
              values={{
                connectionName: (
                  <Typography component="span" variant="body2" color="textPrimary">
                    {connections[0].name}
                  </Typography>
                )
              }}
            />
          ) : (
            <FormattedMessage id="connections.credentialsDialog.delete.questionMultiple" />
          )}
        </Typography>

        {errors?.map((error, i) => {
          const connectionName = connections.find((connection) => connection.id === error.connectionId)?.name
          return (
            <Box key={i}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                <FormattedMessage
                  id="connections.credentialsDialog.delete.error"
                  values={{
                    connectionName: (
                      <Typography component="span" variant="body2" color="textPrimary">
                        {connectionName}
                      </Typography>
                    )
                  }}
                />
              </Typography>
              <Alert severity="error">{error.error?.stack}</Alert>
            </Box>
          )
        })}
      </DialogContent>
      <DialogFooter
        alertContent={<FormattedMessage id="connections.credentialsDialog.delete.info" />}
        alertProps={{ severity: 'info' }}
      >
        <Button onClick={() => onClose()} color="default">
          <FormattedMessage id="common.button.cancel" />
        </Button>
        <Button
          {...{ 'aria-label': 'remove-credentials-confirm-button' }}
          onClick={handleClickDelete}
          loading={isLoading}
          color="error"
          variant="outlined"
        >
          <FormattedMessage id="connections.credentialsDialog.delete.actionButton" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default DeleteCredentialsDialog
